/**
 * Returns true when `error` looks like a browser-side network failure (the
 * device is offline, DNS resolution failed, the server is unreachable,
 * CORS blocked the response, etc.) — as opposed to a real HTTP response
 * the server sent back.
 *
 * Why this matters: `fetch()` resolves successfully for any HTTP status —
 * 200, 404, 500, all the same. It only *throws* when the request couldn't
 * complete a round-trip. So a thrown `TypeError` is a strong signal that
 * the problem is local to the user (connection / proxy / etc.), and
 * surfacing a "Check your connection" hint is more useful than a generic
 * "please try again". Conversely, an `ApiError` from our `api.ts` wrapper
 * means the server replied (the failure happened on the backend) — we
 * shouldn't blame the user's connection there.
 *
 * Detection:
 * - `error instanceof TypeError` — fetch throws a `TypeError` for network
 *   failures across every browser; this catches the typical case without
 *   relying on the message string.
 * - Message regex — defensive backstop for cases where something
 *   re-wraps the original error (e.g. Promise chains that lose the
 *   prototype, or third-party libs).
 *
 * Known message variants we match:
 * - Chrome / Edge: `"Failed to fetch"`
 * - Firefox:      `"NetworkError when attempting to fetch resource."`
 * - Safari:       `"Load failed"`
 */
export function errorIsNetwork(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (error instanceof Error) {
    return /failed to fetch|networkerror|load failed/i.test(error.message);
  }
  return false;
}
